<template>
  <div class="sa-voice-admin">
    <header class="sa-voice-admin-head">
      <div>
        <h1 class="sa-voice-admin-h1">Voice usage</h1>
        <p class="sa-voice-admin-sub">
          Which labs and people are using voice, and what happened when they did.
        </p>
      </div>

      <!-- The range picker. Real buttons, not a listbox: five options that all
           fit on one line are faster to reach with a keyboard than anything
           that has to be opened first. -->
      <div class="sa-voice-admin-range" role="group" aria-label="Date range">
        <button
          v-for="option in presets"
          :key="option.key"
          type="button"
          class="sa-voice-admin-chip"
          :class="{ 'sa-voice-admin-chip-on': preset == option.key }"
          :aria-pressed="preset == option.key ? 'true' : 'false'"
          @click="choosePreset(option.key)"
        >
          {{ option.label }}
        </button>

        <span v-if="preset == 'custom'" class="sa-voice-admin-custom">
          <label class="sa-voice-admin-label" for="sa-voice-admin-from">From</label>
          <input id="sa-voice-admin-from" v-model="customFrom" type="date" />
          <label class="sa-voice-admin-label" for="sa-voice-admin-to">To</label>
          <input id="sa-voice-admin-to" v-model="customTo" type="date" />
        </span>

        <button
          type="button"
          class="sa-voice-admin-chip"
          :disabled="overviewLoading"
          @click="reload"
        >
          Reload
        </button>
      </div>
    </header>

    <p class="sa-voice-admin-rangeline">
      <template v-if="range.pending">
        Pick both dates to load a custom range.
      </template>
      <template v-else>
        {{ formatExact(range.since) }} &ndash; {{ formatExact(range.until) }}
      </template>
    </p>

    <!-- Loading says nothing about numbers. "0 sessions" and "not loaded yet"
         are different facts and the screen must not confuse them. -->
    <p v-if="overviewLoading" class="sa-voice-admin-muted">Loading voice usage&hellip;</p>

    <section
      v-else-if="overviewError"
      class="sa-voice-admin-panel"
      :class="overviewError.danger ? 'sa-voice-admin-panel-danger' : 'sa-voice-admin-panel-warning'"
    >
      <p class="sa-voice-admin-panel-title">{{ overviewError.title }}</p>
      <p class="sa-voice-admin-panel-detail">{{ overviewError.detail }}</p>
      <p class="sa-voice-admin-panel-next">{{ overviewError.next }}</p>
      <button type="button" class="sa-voice-admin-btn" @click="reload">Try again</button>
    </section>

    <section v-else-if="overview.empty" class="sa-voice-admin-panel">
      <p class="sa-voice-admin-panel-title">Nobody used voice in this range.</p>
      <p class="sa-voice-admin-panel-detail">
        No sessions, no turns and no cost between
        {{ formatExact(range.since) }} and {{ formatExact(range.until) }}.
      </p>
      <p class="sa-voice-admin-panel-next">Widen the range, or check a different period.</p>
    </section>

    <template v-else>
      <!-- ---------------------------------------------------------------- -->
      <!-- Totals                                                            -->
      <!-- ---------------------------------------------------------------- -->
      <ul class="sa-voice-admin-tiles">
        <li class="sa-voice-admin-tile">
          <span class="sa-voice-admin-tile-label">Sessions</span>
          <span class="sa-voice-admin-tile-value">{{ formatCount(totals.sessions) }}</span>
        </li>
        <li class="sa-voice-admin-tile">
          <span class="sa-voice-admin-tile-label">Audio minutes</span>
          <span class="sa-voice-admin-tile-value">{{ formatMinutes(totals.audioSeconds) }}</span>
        </li>
        <li class="sa-voice-admin-tile">
          <span class="sa-voice-admin-tile-label">Turns</span>
          <span class="sa-voice-admin-tile-value">{{ formatCount(totals.turns) }}</span>
        </li>
        <li class="sa-voice-admin-tile">
          <span class="sa-voice-admin-tile-label">Refusals</span>
          <span class="sa-voice-admin-tile-value">{{ formatCount(totals.refusals) }}</span>
          <span class="sa-voice-admin-tile-sub">
            {{ formatPct(ratio(totals.refusals, totals.turns)) }} of turns
          </span>
        </li>
        <li class="sa-voice-admin-tile">
          <span class="sa-voice-admin-tile-label">Edits</span>
          <span class="sa-voice-admin-tile-value">{{ formatCount(totals.edits) }}</span>
          <span class="sa-voice-admin-tile-sub">transcripts corrected by hand</span>
        </li>

        <!-- The cost tile carries its own split. `tabindex` is not decoration:
             a hover-only disclosure is unreachable by keyboard, and the CSS
             opens this on focus-within as well as on hover. -->
        <li class="sa-voice-admin-tile sa-voice-admin-tile-pop" tabindex="0">
          <span class="sa-voice-admin-tile-label">Cost</span>
          <span class="sa-voice-admin-tile-value">{{ formatUsd(totals.costUsd) }}</span>
          <span class="sa-voice-admin-tile-sub">by arm &mdash; hover or focus</span>
          <div class="sa-voice-admin-pop" role="note">
            <p class="sa-voice-admin-pop-title">Cost by arm</p>
            <div v-for="arm in arms" :key="arm.key" class="sa-voice-admin-pop-row">
              <span class="sa-voice-admin-pop-name">{{ arm.label }}</span>
              <span class="sa-voice-admin-bar">
                <span class="sa-voice-admin-bar-fill" :style="{ width: arm.pct + '%' }" />
              </span>
              <span class="sa-voice-admin-pop-value">{{ formatUsd(arm.usd) }}</span>
            </div>
            <!-- Codex is billed by subscription and has no marginal price, so
                 its arm is always exactly $0.00. Unexplained, a zero next to a
                 busy arm reads as a broken meter; the count is the figure that
                 means anything for this one. -->
            <p v-if="codexIsFree" class="sa-voice-admin-pop-note">
              Codex is subscription-billed, so it has no per-parse price.
              {{ formatCount(totals.parsesBySource.codex) }} parses came from it.
            </p>
          </div>
        </li>
      </ul>

      <!-- ---------------------------------------------------------------- -->
      <!-- Funnel, parse sources, latency                                    -->
      <!-- ---------------------------------------------------------------- -->
      <div class="sa-voice-admin-cols">
        <section class="sa-voice-admin-card">
          <h2 class="sa-voice-admin-h2">Said &rarr; previewed &rarr; applied</h2>
          <div v-for="stage in funnel" :key="stage.key" class="sa-voice-admin-funnel-row">
            <span class="sa-voice-admin-funnel-name">{{ stage.label }}</span>
            <span class="sa-voice-admin-bar sa-voice-admin-bar-tall">
              <span class="sa-voice-admin-bar-fill" :style="{ width: stage.width + '%' }" />
            </span>
            <span class="sa-voice-admin-funnel-value">
              {{ formatCount(stage.count) }}
              <span class="sa-voice-admin-funnel-sub">{{ formatPct(stage.pctOfFirst) }}</span>
            </span>
          </div>
          <p v-if="!funnelGap" class="sa-voice-admin-note">
            {{ formatCount(funnel[1].dropped) }} never reached a preview,
            {{ formatCount(funnel[2].dropped) }} were previewed and not applied.
          </p>
          <!-- A later stage ahead of an earlier one is a real state, not a
               rendering fault, and it will persist for a while: a command
               applied before the turn log existed has no turn to link to, and
               until the dock sends its turn id the server falls back to
               matching parse arguments, which misses whenever the dock's
               resolved identifiers differ from the parser's raw ones. Saying so
               is the difference between an admin reading a known gap and an
               admin reporting the page as broken. -->
          <p v-else class="sa-voice-admin-note sa-voice-admin-note-flag">
            {{ funnelGap.stage }} is {{ formatCount(funnelGap.missing) }} ahead of
            {{ funnelGap.previous }}, which cannot happen on one turn&rsquo;s journey.
            That many were counted without a matching earlier step: applied before
            this log existed, or applied through a preview the server could not tie
            back to its turn. Both figures are the server&rsquo;s own; neither is
            adjusted to tidy the funnel.
          </p>
        </section>

        <section class="sa-voice-admin-card">
          <h2 class="sa-voice-admin-h2">Where the parse came from</h2>
          <!-- One bar, four segments. Never colour alone: every segment prints
               its own count in the legend underneath. -->
          <div class="sa-voice-admin-stack" role="img" :aria-label="sourceSummary">
            <span
              v-for="segment in segments"
              :key="segment.key"
              class="sa-voice-admin-stack-seg"
              :class="'sa-voice-admin-src-' + segment.key"
              :style="{ width: segment.width + '%' }"
              :title="segment.label + ': ' + formatCount(segment.count)"
            />
          </div>
          <ul class="sa-voice-admin-legend">
            <li v-for="segment in segments" :key="segment.key">
              <span
                class="sa-voice-admin-swatch"
                :class="'sa-voice-admin-src-' + segment.key"
                aria-hidden="true"
              />
              {{ segment.label }}
              <strong>{{ formatCount(segment.count) }}</strong>
              <span class="sa-voice-admin-muted">{{ formatPct(segment.pct) }}</span>
            </li>
          </ul>
          <p class="sa-voice-admin-note">
            Parse time p50 <strong>{{ formatMs(totals.p50ParseMs) }}</strong>,
            p90 <strong>{{ formatMs(totals.p90ParseMs) }}</strong>.
          </p>

          <!-- Won is only half the story: the arms are billed on being ASKED,
               and the guard never wins anything at all. An arm consulted four
               times for every parse it wins is a cost the stacked bar above
               cannot show, because that bar only has room for winners. -->
          <template v-if="overview.armsPresent">
            <p class="sa-voice-admin-note">Asked, and how often it won:</p>
            <ul class="sa-voice-admin-arms">
              <li v-for="arm in armRows" :key="arm.key">
                <span class="sa-voice-admin-arm-name">{{ arm.label }}</span>
                <span class="sa-voice-admin-bar">
                  <span
                    class="sa-voice-admin-bar-fill"
                    :style="{ width: arm.width + '%' }"
                  />
                </span>
                <span class="sa-voice-admin-arm-value">
                  {{ formatCount(arm.won) }} / {{ formatCount(arm.consulted) }}
                  <span class="sa-voice-admin-muted">{{ formatPct(arm.winPct) }}</span>
                </span>
              </li>
            </ul>
          </template>
        </section>

        <section class="sa-voice-admin-card">
          <h2 class="sa-voice-admin-h2">Why voice refused</h2>
          <p v-if="!refusalRows.length" class="sa-voice-admin-muted">
            No refusals in this range.
          </p>
          <!-- Code and count on one line, the bar on its own beneath it. Three
               columns in a third of the page leaves the bar a few pixels wide,
               which is a decoration rather than a comparison. -->
          <div v-for="row in refusalRows" :key="row.code" class="sa-voice-admin-refusal">
            <span class="sa-voice-admin-refusal-head">
              <span class="sa-voice-admin-code" :title="row.code">{{ row.code }}</span>
              <span class="sa-voice-admin-refusal-value">
                {{ formatCount(row.count) }}
                <span class="sa-voice-admin-muted">{{ formatPct(row.pct) }}</span>
              </span>
            </span>
            <span class="sa-voice-admin-bar">
              <span
                class="sa-voice-admin-bar-fill sa-voice-admin-bar-warn"
                :style="{ width: row.pct + '%' }"
              />
            </span>
          </div>
        </section>
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- Where the time goes, and why the grammar gave up                  -->
      <!-- ---------------------------------------------------------------- -->
      <div class="sa-voice-admin-pair">
        <section class="sa-voice-admin-card">
          <h2 class="sa-voice-admin-h2">
            Where the time goes
            <span class="sa-voice-admin-muted">the dock&rsquo;s own clock</span>
          </h2>

          <!-- Until the backend release lands there is no `stages` block at
               all, and nine empty bars would read as "every stage of voice is
               instant" — the opposite of the truth and drawn from no data. -->
          <p v-if="!overview.stagesPresent" class="sa-voice-admin-muted">
            Not available yet. The dock sends these spans on every turn; they
            appear here with the backend release that stores them.
          </p>
          <template v-else>
            <p class="sa-voice-admin-note">
              Ranked by p95 &mdash; the tail is what people report as slow. Dark
              is the median, pale is p95, and every bar is on one scale.
            </p>
            <div
              v-for="stage in stageRows"
              :key="stage.key"
              class="sa-voice-admin-stage"
            >
              <span class="sa-voice-admin-stage-head">
                <span class="sa-voice-admin-stage-name" :title="stage.key">
                  {{ stage.label }}
                </span>
                <span class="sa-voice-admin-stage-value">
                  {{ formatMs(stage.p50) }}
                  <span class="sa-voice-admin-muted">
                    <!-- A stage on the ledger's ceiling is not a measurement of
                         two minutes; it is "at least two minutes", which is
                         what a backgrounded tab leaves behind. Printing the
                         ceiling as a figure would invite somebody to go and
                         optimise a number that was never measured. -->
                    p95 <template v-if="stage.atCeiling">&ge;&nbsp;</template
                    >{{ formatMs(stage.p95) }} &middot;
                    n {{ formatCount(stage.n) }}
                  </span>
                </span>
              </span>
              <!-- Two bars sharing one track, both measured from the left, so
                   the gap between them IS the spread. Never colour alone: the
                   two numbers are printed above it. -->
              <span
                class="sa-voice-admin-track"
                :class="{ 'sa-voice-admin-track-over': stage.atCeiling }"
                role="img"
                :aria-label="stageSummary(stage)"
              >
                <span
                  class="sa-voice-admin-track-p95"
                  :style="{ width: stage.width95 + '%' }"
                />
                <span
                  class="sa-voice-admin-track-p50"
                  :style="{ width: stage.width50 + '%' }"
                />
              </span>
            </div>
            <!-- Only when there is one. A standing sentence about a ceiling
                 nobody has hit is a sentence that teaches readers to skip the
                 notes under this card. -->
            <p v-if="stagesAtCeiling" class="sa-voice-admin-note sa-voice-admin-note-flag">
              {{ ceilingNote }} on the two-minute ceiling the dock clamps to, so
              the bar runs off the scale and every other row is drawn against the
              longest real measurement instead. That is a turn whose tab was
              backgrounded mid-command, not a stage that takes two minutes.
            </p>
            <p class="sa-voice-admin-note">
              Client spans only, each the difference between two readings of one
              browser&rsquo;s monotonic clock. Nothing here is a client time
              minus a server time.
            </p>
          </template>
        </section>

        <section class="sa-voice-admin-card">
          <h2 class="sa-voice-admin-h2">
            Why the grammar gave up
            <span class="sa-voice-admin-muted">pick one to see the turns</span>
          </h2>

          <p v-if="!overview.abstainsPresent" class="sa-voice-admin-muted">
            Not available yet. Abstention codes arrive with the backend release
            that maps the grammar&rsquo;s reasons onto a closed list.
          </p>
          <p v-else-if="!abstainRows.length" class="sa-voice-admin-muted">
            No stage abstained in this range.
          </p>
          <!-- A real button, not a clickable div: this filters the log below
               it, so it has to be reachable and announceable as a control. -->
          <button
            v-for="row in abstainRows"
            :key="row.code"
            type="button"
            class="sa-voice-admin-abstain"
            :class="{ 'sa-voice-admin-abstain-on': filters.abstainCode == row.code }"
            :aria-pressed="filters.abstainCode == row.code ? 'true' : 'false'"
            @click="chooseAbstain(row.code)"
          >
            <span class="sa-voice-admin-refusal-head">
              <span class="sa-voice-admin-abstain-label">{{ row.label }}</span>
              <span class="sa-voice-admin-refusal-value">
                {{ formatCount(row.count) }}
                <span class="sa-voice-admin-muted">{{ formatPct(row.pct) }}</span>
              </span>
            </span>
            <!-- The enum name as well as the sentence: the sentence is for
                 deciding what to build, the code is what gets grepped. -->
            <span class="sa-voice-admin-code" :title="row.code">{{ row.code }}</span>
            <span class="sa-voice-admin-bar">
              <span
                class="sa-voice-admin-bar-fill sa-voice-admin-bar-warn"
                :style="{ width: row.width + '%' }"
              />
            </span>
          </button>
        </section>
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- Labs                                                              -->
      <!-- ---------------------------------------------------------------- -->
      <section class="sa-voice-admin-card">
        <h2 class="sa-voice-admin-h2">
          Labs
          <span class="sa-voice-admin-muted">{{ labRows.length }}</span>
        </h2>
        <div class="sa-voice-admin-scroll">
          <table class="sa-voice-admin-table">
            <thead>
              <tr>
                <th scope="col">
                  <button type="button" class="sa-voice-admin-sort" @click="sortLabs('name')">
                    Lab{{ sortMark(labSort, 'name') }}
                  </button>
                </th>
                <th v-for="col in numericColumns" :key="col.key" scope="col">
                  <button type="button" class="sa-voice-admin-sort" @click="sortLabs(col.key)">
                    {{ col.label }}{{ sortMark(labSort, col.key) }}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in labRows"
                :key="row.key"
                class="sa-voice-admin-row"
                :class="{ 'sa-voice-admin-row-on': row.labId == selectedLabId }"
                tabindex="0"
                role="button"
                :aria-pressed="row.labId == selectedLabId ? 'true' : 'false'"
                @click="selectLab(row.labId)"
                @keydown.enter.prevent="selectLab(row.labId)"
                @keydown.space.prevent="selectLab(row.labId)"
              >
                <td class="sa-voice-admin-name">{{ row.name }}</td>
                <td>{{ formatCount(row.sessions) }}</td>
                <td>{{ formatMinutes(row.audioSeconds) }}</td>
                <td>{{ formatCount(row.turns) }}</td>
                <td>{{ formatCount(row.commands) }}</td>
                <td>{{ formatCount(row.applies) }}</td>
                <td>{{ formatCount(row.refusals) }}</td>
                <td>{{ formatCount(row.edits) }}</td>
                <td>{{ formatUsd(row.costUsd) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ---------------------------------------------------------------- -->
      <!-- Users                                                             -->
      <!-- ---------------------------------------------------------------- -->
      <section class="sa-voice-admin-card">
        <h2 class="sa-voice-admin-h2">
          People
          <span class="sa-voice-admin-muted">{{ userRows.length }}</span>
          <template v-if="selectedLabId">
            <span class="sa-voice-admin-muted">in {{ selectedLabName }}</span>
            <button type="button" class="sa-voice-admin-chip" @click="selectLab('')">
              Show all labs
            </button>
          </template>
        </h2>
        <div class="sa-voice-admin-scroll">
          <table class="sa-voice-admin-table">
            <thead>
              <tr>
                <th scope="col">
                  <button type="button" class="sa-voice-admin-sort" @click="sortUsers('name')">
                    Person{{ sortMark(userSort, 'name') }}
                  </button>
                </th>
                <th scope="col">Lab</th>
                <th scope="col">
                  <button type="button" class="sa-voice-admin-sort" @click="sortUsers('lastAt')">
                    Last active{{ sortMark(userSort, 'lastAt') }}
                  </button>
                </th>
                <th v-for="col in userColumns" :key="col.key" scope="col">
                  <button type="button" class="sa-voice-admin-sort" @click="sortUsers(col.key)">
                    {{ col.label }}{{ sortMark(userSort, col.key) }}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in userRows"
                :key="row.key"
                class="sa-voice-admin-row"
                :class="{ 'sa-voice-admin-row-on': row.userId == selectedUserId }"
                tabindex="0"
                role="button"
                :aria-pressed="row.userId == selectedUserId ? 'true' : 'false'"
                @click="selectUser(row)"
                @keydown.enter.prevent="selectUser(row)"
                @keydown.space.prevent="selectUser(row)"
              >
                <td class="sa-voice-admin-name">
                  {{ row.name }}
                  <span class="sa-voice-admin-email">{{ row.email }}</span>
                </td>
                <td>{{ row.labName }}</td>
                <td :title="formatExact(row.lastAt)">{{ formatWhen(row.lastAt, now) }}</td>
                <td>{{ formatCount(row.sessions) }}</td>
                <td>{{ formatCount(row.turns) }}</td>
                <td>{{ formatCount(row.applies) }}</td>
                <td>{{ formatCount(row.refusals) }}</td>
                <td>{{ formatUsd(row.costUsd) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- ------------------------------------------------------------------ -->
    <!-- Turns. Its own loading and error state, because the overview and the -->
    <!-- log are two endpoints and one of them can be down while the other is -->
    <!-- fine — a single spinner over both would hide that.                   -->
    <!-- ------------------------------------------------------------------ -->
    <section ref="turnsSection" class="sa-voice-admin-card">
      <h2 class="sa-voice-admin-h2">What was said</h2>

      <!-- Each label and its control are one inline-flex box, so a wrap moves
           the pair and never leaves a label stranded above someone else's
           select. -->
      <div class="sa-voice-admin-filters">
        <span class="sa-voice-admin-field">
          <label class="sa-voice-admin-label" for="sa-voice-admin-lab">Lab</label>
          <select id="sa-voice-admin-lab" v-model="selectedLabId" @change="onLabFilter">
            <option value="">All labs</option>
            <option v-for="opt in labOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </span>

        <span class="sa-voice-admin-field">
          <label class="sa-voice-admin-label" for="sa-voice-admin-user">Person</label>
          <select id="sa-voice-admin-user" v-model="selectedUserId" @change="loadTurns()">
            <option value="">Everyone</option>
            <option v-for="opt in userOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </span>

        <span class="sa-voice-admin-field">
          <label class="sa-voice-admin-label" for="sa-voice-admin-kind">Kind</label>
          <select id="sa-voice-admin-kind" v-model="filters.kind" @change="onFilter">
            <option value="">Any</option>
            <option v-for="kind in kinds" :key="kind.key" :value="kind.key">{{ kind.label }}</option>
          </select>
        </span>

        <span class="sa-voice-admin-field">
          <label class="sa-voice-admin-label" for="sa-voice-admin-source">Source</label>
          <select id="sa-voice-admin-source" v-model="filters.source" @change="onFilter">
            <option value="">Any</option>
            <option v-for="src in sources" :key="src.key" :value="src.key">{{ src.label }}</option>
          </select>
        </span>

        <span class="sa-voice-admin-field">
          <label class="sa-voice-admin-label" for="sa-voice-admin-outcome">Outcome</label>
          <select id="sa-voice-admin-outcome" v-model="filters.outcome" @change="onFilter">
            <option value="">Any</option>
            <option v-for="o in outcomes" :key="o.key" :value="o.key">{{ o.label }}</option>
          </select>
        </span>

        <label class="sa-voice-admin-check">
          <input v-model="filters.refusedOnly" type="checkbox" @change="onFilter" />
          Refused only
        </label>

        <!-- Set by a click on the card above rather than chosen here, so it is
             shown as what it is: a filter in force, with the way off it. A
             filter that is invisible while it is working is the thing that
             makes a short list read as "these are all the turns". -->
        <span v-if="filters.abstainCode" class="sa-voice-admin-field">
          <span class="sa-voice-admin-label">Gave up because</span>
          <button
            type="button"
            class="sa-voice-admin-chip sa-voice-admin-chip-on"
            @click="chooseAbstain(filters.abstainCode)"
          >
            {{ filters.abstainCode }} &times;
          </button>
        </span>

        <!-- Not a filter: a different QUESTION. Rows answer "what did this
             person say"; shapes answer "what are people saying that we do not
             understand", which is the one a backlog is written from. -->
        <button
          type="button"
          class="sa-voice-admin-chip"
          :class="{ 'sa-voice-admin-chip-on': shapesOn }"
          :aria-pressed="shapesOn ? 'true' : 'false'"
          @click="toggleShapes"
        >
          Shapes
        </button>

        <span class="sa-voice-admin-field">
          <label class="sa-voice-admin-label" for="sa-voice-admin-q">Search</label>
          <input
            id="sa-voice-admin-q"
            v-model="filters.q"
            type="search"
            placeholder="words in the transcript"
            @keydown.enter.prevent="loadTurns()"
            @input="searchLater"
          />
        </span>

        <button type="button" class="sa-voice-admin-chip" @click="clearFilters">Clear</button>
      </div>

      <!-- ---------------------------------------------------------------- -->
      <!-- Shapes                                                            -->
      <!-- ---------------------------------------------------------------- -->
      <template v-if="shapesOn">
        <p v-if="shapesLoading" class="sa-voice-admin-muted">Grouping turns&hellip;</p>

        <div
          v-else-if="shapesError"
          class="sa-voice-admin-panel"
          :class="shapesError.danger ? 'sa-voice-admin-panel-danger' : 'sa-voice-admin-panel-warning'"
        >
          <p class="sa-voice-admin-panel-title">{{ shapesError.title }}</p>
          <p class="sa-voice-admin-panel-detail">{{ shapesError.detail }}</p>
          <p class="sa-voice-admin-panel-next">{{ shapesError.next }}</p>
          <button type="button" class="sa-voice-admin-btn" @click="loadShapes()">
            Try again
          </button>
        </div>

        <!-- A server that does not know `shape=1` answers with ordinary rows.
             Saying "no shapes" there would be a lie about the data; this says
             what is actually true, which is that the feature is not deployed. -->
        <p v-else-if="!shapesSupported" class="sa-voice-admin-muted">
          Shapes are not available yet. They arrive with the backend release
          that groups the transcript log.
        </p>

        <p v-else-if="!shapes.length" class="sa-voice-admin-muted">
          Nothing was left ungrasped in this range.
        </p>

        <ol v-else class="sa-voice-admin-shapes">
          <li v-for="row in shapes" :key="row.shape" class="sa-voice-admin-shape">
            <div class="sa-voice-admin-shape-head">
              <span class="sa-voice-admin-shape-count">{{ formatCount(row.count) }}</span>
              <code class="sa-voice-admin-shape-name">{{ row.shape }}</code>
              <span class="sa-voice-admin-muted">{{ formatPct(row.pct) }}</span>
              <!-- Why this shape kept failing, every cause of it. A shape said
                   eight times that died two different ways is a queue item with
                   two fixes on it, so the list is never cut to the first. Each
                   one filters the log, the same click the card above makes. -->
              <button
                v-for="cause in row.abstainCodes"
                :key="cause.code"
                type="button"
                class="sa-voice-admin-tag sa-voice-admin-tag-link"
                :title="causeTitle(cause)"
                @click.stop="chooseAbstain(cause.code)"
              >
                {{ cause.code }}
                <span v-if="cause.count !== null" class="sa-voice-admin-muted">
                  {{ formatCount(cause.count) }}
                </span>
              </button>
            </div>
            <span class="sa-voice-admin-bar">
              <span
                class="sa-voice-admin-bar-fill sa-voice-admin-bar-warn"
                :style="{ width: row.width + '%' }"
              />
            </span>
            <!-- The example is the way back into the log: one click puts it in
                 the search box, which is how a shape becomes the turns behind
                 it. Verbatim, like every transcript on this page. -->
            <button
              type="button"
              class="sa-voice-admin-shape-example"
              :title="row.exampleAt ? formatExact(row.exampleAt) : ''"
              @click="useShape(row)"
            >
              &ldquo;{{ row.example }}&rdquo;
            </button>
          </li>
        </ol>
      </template>

      <template v-else>
      <p v-if="turnsLoading && !turns.length" class="sa-voice-admin-muted">Loading turns&hellip;</p>

      <div
        v-else-if="turnsError"
        class="sa-voice-admin-panel"
        :class="turnsError.danger ? 'sa-voice-admin-panel-danger' : 'sa-voice-admin-panel-warning'"
      >
        <p class="sa-voice-admin-panel-title">{{ turnsError.title }}</p>
        <p class="sa-voice-admin-panel-detail">{{ turnsError.detail }}</p>
        <p class="sa-voice-admin-panel-next">{{ turnsError.next }}</p>
        <button type="button" class="sa-voice-admin-btn" @click="loadTurns()">Try again</button>
      </div>

      <p v-else-if="!turns.length" class="sa-voice-admin-muted">
        No turns match these filters in this range.
      </p>

      <ol v-else class="sa-voice-admin-turns">
        <li
          v-for="turn in turns"
          :key="turn.id"
          class="sa-voice-admin-turn"
          :class="{ 'sa-voice-admin-turn-refused': turn.refused }"
        >
          <div class="sa-voice-admin-turn-head">
            <span class="sa-voice-admin-turn-at" :title="formatExact(turn.at)">
              {{ formatWhen(turn.at, now) }}
            </span>
            <span class="sa-voice-admin-turn-who">
              {{ turn.userName || "(unknown)" }}
              <span class="sa-voice-admin-email">{{ turn.userEmail }}</span>
            </span>
            <span class="sa-voice-admin-turn-lab">{{ turn.labName }}</span>
            <span class="sa-voice-admin-tag">{{ turn.kind }}</span>
            <span v-if="turn.channel == 'http'" class="sa-voice-admin-tag">http</span>
            <span v-if="turn.phone" class="sa-voice-admin-tag">phone</span>
            <span v-if="turn.injectionFlag" class="sa-voice-admin-tag sa-voice-admin-tag-warn">
              injection flagged
            </span>
            <button
              type="button"
              class="sa-voice-admin-chip sa-voice-admin-chip-small"
              :aria-expanded="expanded[turn.id] ? 'true' : 'false'"
              @click="toggle(turn.id)"
            >
              {{ expanded[turn.id] ? "Hide JSON" : "JSON" }}
            </button>
          </div>

          <!-- Verbatim. The point of the collection is that this is what was
               actually said, so it is rendered as text and never tidied. -->
          <p class="sa-voice-admin-transcript">{{ turn.transcript }}</p>

          <div class="sa-voice-admin-turn-meta">
            <span v-if="turn.tool" class="sa-voice-admin-parse">
              <strong>{{ turn.tool }}</strong>
              <span class="sa-voice-admin-args">{{ turn.argsSummary }}</span>
            </span>
            <span v-else class="sa-voice-admin-muted">no tool</span>

            <span class="sa-voice-admin-tag">{{ turn.source || "?" }}</span>
            <span v-if="turn.confidence !== null" class="sa-voice-admin-muted">
              conf {{ formatConfidence(turn.confidence) }}
            </span>
            <span v-if="turn.latencyMs !== null" class="sa-voice-admin-muted">
              {{ formatMs(turn.latencyMs) }}
            </span>
            <span v-if="turn.grammarAbstained" class="sa-voice-admin-muted">grammar abstained</span>
            <!-- The code, not the sentence: at row density the enum is the
                 thing that is scanned for, and the sentence is a click away on
                 the card above. Clicking it filters the log to its siblings. -->
            <button
              v-if="turn.abstainCode"
              type="button"
              class="sa-voice-admin-tag sa-voice-admin-tag-link"
              :title="turn.abstainDetail || turn.abstainCode"
              @click="chooseAbstain(turn.abstainCode)"
            >
              {{ turn.abstainCode }}
            </button>
            <span
              v-if="turn.outcome && turn.outcome != 'PROPOSAL'"
              class="sa-voice-admin-tag"
            >
              {{ outcomeLabel(turn.outcome) }}
            </span>
            <span v-if="turn.editedFrom" class="sa-voice-admin-muted">
              corrects {{ turn.editedFrom }}
            </span>
            <span v-if="turn.refusalCode" class="sa-voice-admin-tag sa-voice-admin-tag-warn">
              {{ turn.refusalCode }}
            </span>
            <span
              v-if="turn.receiptId"
              class="sa-voice-admin-tag"
              :title="'Command receipt ' + turn.receiptId"
            >
              receipt {{ turn.receiptId }}
            </span>
          </div>

          <!-- One line for the whole escalation, in the order the arms were
               asked. A turn that walked grammar -> Jev -> Gemini used to be
               three facts in three places; it is the single most useful line
               on the row and it belongs next to the sentence. -->
          <p v-if="turn.arms.length > 1" class="sa-voice-admin-arms-trail">
            <span v-for="(arm, i) in turn.arms" :key="arm.arm + i">
              <span v-if="i" aria-hidden="true"> &rarr; </span>
              <strong>{{ arm.arm }}</strong>
              {{ armOutcome(arm) }}
              <span v-if="arm.latencyMs !== null" class="sa-voice-admin-muted">
                {{ formatMs(arm.latencyMs) }}
              </span>
            </span>
          </p>

          <!-- The dock's own spans for THIS turn, and only the ones it really
               measured. Six em dashes would bury the two that say where the
               eleven seconds went. -->
          <p v-if="turn.timing.length" class="sa-voice-admin-turn-timing">
            <span v-for="span in turn.timing" :key="span.key" :title="span.label">
              {{ span.label }} <strong>{{ formatMs(span.ms) }}</strong>
            </span>
          </p>

          <p v-if="turn.refusalReason" class="sa-voice-admin-reason">{{ turn.refusalReason }}</p>
          <p v-if="turn.readbackText" class="sa-voice-admin-readback">
            &ldquo;{{ turn.readbackText }}&rdquo;
            <span v-if="turn.readbackCached" class="sa-voice-admin-muted">cached</span>
          </p>

          <pre v-if="expanded[turn.id]" class="sa-voice-admin-json">{{ json(turn) }}</pre>
        </li>
      </ol>

      <div v-if="turns.length" class="sa-voice-admin-more">
        <button
          v-if="turnsBefore"
          type="button"
          class="sa-voice-admin-btn"
          :disabled="turnsLoading"
          @click="loadTurns(true)"
        >
          {{ turnsLoading ? "Loading…" : "Load more" }}
        </button>
        <span v-else class="sa-voice-admin-muted">
          {{ formatCount(turns.length) }} turns &mdash; that is all of them.
        </span>
      </div>
      </template>
    </section>
  </div>
</template>

<script>
/**
 * The super-admin view of who is using voice and how.
 *
 * Two endpoints, from FIX-B6 items 5 and 7: an aggregated overview for the
 * range, and the turn log it drills into. Both go through the single
 * `fetchVoiceAdmin` store action, which is where the transport and the error
 * classification already live for every other voice call.
 *
 * All the arithmetic — ranges, percentages, row shaping, query strings — is in
 * `utils/voiceAdminModel.js` and tested by `scripts/test-voice-admin.js`. What
 * is left here is markup, the fetches, and the selection state that ties the
 * three views together.
 *
 * TWO THINGS THE COPY GETS RIGHT ON PURPOSE.
 *
 * A 404 from either endpoint is `voiceFetch`'s `missing` class, whose standard
 * sentence is "That box was changed or removed by someone else." That is
 * nonsense here: on this page a 404 means the backend packet that adds these
 * endpoints has not been deployed yet. The same goes for 403, whose standard
 * sentence talks about the voice beta when what it actually means here is that
 * the caller is not a super admin. Both are re-worded below rather than passed
 * through, because a wrong explanation is worse than a bare status code.
 *
 * The overview and the turns keep separate loading and error state. They are
 * two requests, and one of them failing while the other succeeds is a fact the
 * admin needs to see, not something to hide behind a single spinner.
 */
import {
  RANGE_PRESETS,
  PARSE_SOURCES,
  PARSE_OUTCOMES,
  ABSTAIN_CODES,
  TURN_KINDS,
  DEFAULT_PRESET,
  DEFAULT_TURN_LIMIT,
  rangeFor,
  shapeOverview,
  shapeTurns,
  shapeGroups,
  turnsFrom,
  mergeTurns,
  cursorFrom,
  sortRows,
  funnelStages,
  funnelGap,
  costArms,
  sourceSegments,
  refusalCodes,
  emptyTotals,
  ratio,
  formatCount,
  formatUsd,
  formatMinutes,
  formatMs,
  formatPct,
  formatConfidence,
  formatWhen,
  formatExact,
} from "../../../utils/voiceAdminModel.js";

/** The lab table: the same figures as the tiles above it, per lab. */
const NUMERIC_COLUMNS = [
  { key: "sessions", label: "Sessions" },
  { key: "audioSeconds", label: "Audio min" },
  { key: "turns", label: "Turns" },
  { key: "commands", label: "Commands" },
  { key: "applies", label: "Applied" },
  { key: "refusals", label: "Refused" },
  { key: "edits", label: "Edits" },
  { key: "costUsd", label: "Cost" },
];

/**
 * The people table carries fewer: sessions, turns, applies, refusals, cost.
 *
 * Deliberately not the lab set. A person already has three identity columns in
 * front of their numbers, and eleven columns is a table nobody reads — it
 * scrolls sideways at the width this app lays out at, which hides the cost
 * column, the one figure the page exists to surface.
 */
const USER_COLUMNS = [
  { key: "sessions", label: "Sessions" },
  { key: "turns", label: "Turns" },
  { key: "applies", label: "Applied" },
  { key: "refusals", label: "Refused" },
  { key: "costUsd", label: "Cost" },
];

const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: "VoiceAdmin",
  data() {
    return {
      presets: RANGE_PRESETS,
      sources: PARSE_SOURCES,
      kinds: TURN_KINDS,
      numericColumns: NUMERIC_COLUMNS,
      userColumns: USER_COLUMNS,

      outcomes: PARSE_OUTCOMES,

      preset: DEFAULT_PRESET,
      customFrom: "",
      customTo: "",

      overview: null,
      overviewLoading: true,
      overviewError: null,

      labSort: { key: "turns", dir: "desc" },
      userSort: { key: "turns", dir: "desc" },
      selectedLabId: "",
      selectedUserId: "",

      filters: {
        kind: "",
        source: "",
        refusedOnly: false,
        q: "",
        // FIX-B9 item 4. `abstainCode` is set by a click on the card above
        // rather than by a control of its own: the codes are a long closed list
        // and the interesting one is always the one at the top of that card.
        abstainCode: "",
        outcome: "",
      },
      turns: [],
      turnsLoading: false,
      turnsError: null,
      turnsBefore: null,

      // The shapes view. Its own loading and error state for the same reason
      // the turns have theirs: it is a third request, and it can be the one
      // that fails.
      shapesOn: false,
      shapes: [],
      shapesLoading: false,
      shapesError: null,
      // A server that does not know `shape=1` answers with ordinary rows. That
      // is "not deployed", not "nothing to group", and the two must not share a
      // sentence.
      shapesSupported: true,

      expanded: {},
      // Captured once per load rather than read per row: a hundred rows each
      // calling `new Date()` would render "2 min ago" and "3 min ago" for two
      // turns a second apart, purely from how long the render took.
      now: new Date(),
      searchTimer: null,
    };
  },

  computed: {
    range() {
      return rangeFor(
        { preset: this.preset, from: this.customFrom, to: this.customTo },
        this.now
      );
    },
    totals() {
      return this.overview ? this.overview.totals : emptyTotals();
    },
    funnel() {
      return funnelStages(this.totals);
    },
    arms() {
      return costArms(this.totals);
    },
    segments() {
      return sourceSegments(this.totals);
    },
    refusalRows() {
      return refusalCodes(this.totals);
    },
    /**
     * The three FIX-B9 blocks are shaped once, in `shapeOverview`, rather than
     * recomputed here per render. They are read straight off it; the empty
     * defaults keep the template from reading through a null on the first
     * paint and on every failed load.
     */
    stageRows() {
      return this.overview ? this.overview.stages : [];
    },
    /** How many stages are sitting on the clamp, for the note under the card. */
    stagesAtCeiling() {
      return this.stageRows.filter((row) => row.atCeiling).length;
    },
    /** The note's subject, agreeing with its verb. */
    ceilingNote() {
      return this.stagesAtCeiling == 1
        ? "One stage has its p95"
        : `${this.stagesAtCeiling} stages have their p95`;
    },
    abstainRows() {
      return this.overview ? this.overview.abstains : [];
    },
    armRows() {
      return this.overview ? this.overview.arms : [];
    },
    funnelGap() {
      return funnelGap(this.totals);
    },
    /** Only worth explaining when a reader can actually see the puzzle. */
    codexIsFree() {
      return (
        (this.totals.parsesBySource.codex || 0) > 0 &&
        (this.totals.costByArm.codex || 0) === 0
      );
    },
    sourceSummary() {
      return this.segments
        .map((s) => `${s.label} ${formatCount(s.count)}`)
        .join(", ");
    },
    allLabs() {
      return this.overview ? this.overview.labs : [];
    },
    labRows() {
      return sortRows(this.allLabs, this.labSort.key, this.labSort.dir);
    },
    selectedLabName() {
      const lab = this.allLabs.find((row) => row.labId == this.selectedLabId);
      return lab ? lab.name : "";
    },
    /**
     * The people table follows the lab selection. Filtered here rather than
     * refetched: the overview already holds every user of every lab, and a
     * second round trip to hide rows would make a click feel like a page load.
     */
    userRows() {
      const users = this.overview ? this.overview.users : [];
      const scoped = this.selectedLabId
        ? users.filter((row) => row.labId == this.selectedLabId)
        : users;
      return sortRows(scoped, this.userSort.key, this.userSort.dir);
    },
    /**
     * A filter that is still in force must stay visible.
     *
     * When the overview fails — a 404 because the endpoint is not deployed, say
     * — the name lists go with it, but the id is still on every turns request.
     * Without this the select goes blank while the log stays filtered, which
     * reads as "these are all the turns" when they are not. The id is an ugly
     * label; being silently wrong is worse.
     */
    labOptions() {
      const options = this.allLabs.map((row) => ({ value: row.labId, label: row.name }));
      if (this.selectedLabId && !options.some((opt) => opt.value == this.selectedLabId))
        options.push({ value: this.selectedLabId, label: `Lab ${this.selectedLabId}` });
      return options;
    },
    userOptions() {
      const options = this.userRows.map((row) => ({ value: row.userId, label: row.name }));
      if (this.selectedUserId && !options.some((opt) => opt.value == this.selectedUserId))
        options.push({ value: this.selectedUserId, label: `Person ${this.selectedUserId}` });
      return options;
    },
    turnFilters() {
      return {
        since: this.range.since,
        until: this.range.until,
        labId: this.selectedLabId,
        userId: this.selectedUserId,
        kind: this.filters.kind,
        source: this.filters.source,
        refusedOnly: this.filters.refusedOnly,
        abstainCode: this.filters.abstainCode,
        outcome: this.filters.outcome,
        q: this.filters.q,
        limit: DEFAULT_TURN_LIMIT,
      };
    },
  },

  methods: {
    formatCount,
    formatUsd,
    formatMinutes,
    formatMs,
    formatPct,
    formatConfidence,
    formatWhen,
    formatExact,
    ratio,

    json(turn) {
      try {
        return JSON.stringify(turn.raw, null, 2);
      } catch {
        // A cycle cannot come out of JSON over the wire, but the row is also
        // the only copy of a transcript on screen and must not be lost to a
        // throw from a debugging affordance.
        return String(turn.raw);
      }
    },

    sortMark(state, key) {
      if (state.key != key) return "";
      return state.dir == "asc" ? " ↑" : " ↓";
    },

    /** First click on a new column sorts it descending: biggest first is the question. */
    applySort(state, key) {
      if (state.key == key) state.dir = state.dir == "desc" ? "asc" : "desc";
      else {
        state.key = key;
        state.dir = key == "name" ? "asc" : "desc";
      }
    },
    sortLabs(key) {
      this.applySort(this.labSort, key);
    },
    sortUsers(key) {
      this.applySort(this.userSort, key);
    },

    choosePreset(key) {
      this.preset = key;
      if (key != "custom") this.reload();
    },

    selectLab(labId) {
      const next = labId == this.selectedLabId ? "" : labId;
      this.selectedLabId = next;
      // A person from another lab is not a sub-filter of this one, so a lab
      // change always drops the person filter rather than producing a pair that
      // can never match anything.
      this.selectedUserId = "";
      // `onFilter`, not `loadTurns`: picking a lab while the shape list is up
      // is a request to see THAT lab's shapes, not a request to change views.
      this.onFilter();
      this.showTurns();
    },
    onLabFilter() {
      this.selectedUserId = "";
      this.onFilter();
    },
    selectUser(row) {
      this.selectedUserId = row.userId == this.selectedUserId ? "" : row.userId;
      if (this.selectedUserId && row.labId) this.selectedLabId = row.labId;
      this.onFilter();
      this.showTurns();
    },
    clearFilters() {
      this.selectedLabId = "";
      this.selectedUserId = "";
      this.filters = {
        kind: "",
        source: "",
        refusedOnly: false,
        q: "",
        abstainCode: "",
        outcome: "",
      };
      // Clear means clear, including the view: leaving the shapes toggle on
      // under a cleared filter set would answer a question nobody asked twice.
      this.shapesOn = false;
      this.loadTurns();
    },

    /**
     * Any filter control moved.
     *
     * Routed through one method rather than each control calling `loadTurns`,
     * because the shapes view has to follow the same filters — a shape list
     * scoped to a range and a lab, with the Kind select silently ignored, would
     * be three numbers that do not add up to anything.
     */
    onFilter() {
      if (this.shapesOn) this.loadShapes();
      else this.loadTurns();
    },

    /**
     * A code on the "why the grammar gave up" card, or on a row, was clicked.
     *
     * Toggling, so the same click takes it off. It also leaves the shapes view,
     * because the click means "show me these turns" and a shape list is not
     * turns.
     */
    chooseAbstain(code) {
      this.filters.abstainCode = code == this.filters.abstainCode ? "" : code;
      this.shapesOn = false;
      this.loadTurns();
      this.showTurns();
    },

    /**
     * The sentence behind an abstention code, or the code itself.
     *
     * The code is what is shown and the sentence is the title, not the other
     * way round: at this density the enum is what is scanned for, and the
     * server owns the enum — a code this build has never heard of has to render
     * as itself rather than disappear.
     */
    abstainLabel(code) {
      const known = ABSTAIN_CODES.find((c) => c.key == code);
      return known ? known.label : code;
    },

    /** A cause's sentence, with how many of the shape's turns died on it. */
    causeTitle(cause) {
      const label = this.abstainLabel(cause.code);
      return cause.count === null
        ? label
        : `${label} — ${formatCount(cause.count)} of these turns`;
    },

    /** The label for a FIX-B8 outcome, or the value itself if it is new. */
    outcomeLabel(value) {
      const known = PARSE_OUTCOMES.find((o) => o.key == value);
      return known ? known.label : value;
    },

    /** One arm of the escalation trail: what it decided, and why if it said. */
    armOutcome(arm) {
      const code = arm.code ? this.abstainLabel(arm.code) : "";
      const outcome = this.outcomeLabel(arm.outcome) || "\u2014";
      return code ? `${outcome} (${code})` : outcome;
    },

    /** The stage bar's text alternative: the bar is never the only copy. */
    stageSummary(stage) {
      const tail = stage.atCeiling
        ? `p95 at least ${formatMs(stage.p95)}, which is the ledger's ceiling`
        : `p95 ${formatMs(stage.p95)}`;
      return `${stage.label}: median ${formatMs(stage.p50)}, ${tail}, over ${formatCount(
        stage.n
      )} turns`;
    },

    toggleShapes() {
      this.shapesOn = !this.shapesOn;
      if (this.shapesOn) this.loadShapes();
      else this.loadTurns();
    },

    /**
     * A shape was clicked: put its example in the search box and go back to the
     * turns.
     *
     * The example rather than the shape itself. The shape is normalised —
     * numbers to N, serials to P — and searching for "put T in P well N" would
     * match nothing at all; the example is a real sentence, and the words it
     * shares with its siblings are what the text search is for.
     */
    useShape(row) {
      this.filters.q = row.example;
      this.shapesOn = false;
      this.loadTurns();
      this.showTurns();
    },

    /**
     * The grouped shapes for the filters on screen.
     *
     * The same endpoint and the same action as the turns, with `shape=1` added
     * — which is why a server that predates the flag answers with rows instead.
     * That case is detected rather than reported as "nothing to group": rows
     * came back and no shape did, so the feature is simply not deployed.
     */
    loadShapes() {
      if (this.range.pending) return Promise.resolve();
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      this.shapesLoading = true;
      this.shapesError = null;

      const filters = { ...this.turnFilters, shape: true };
      return this.$store
        .dispatch("fetchVoiceAdmin", { view: "turns", filters })
        .then((data) => {
          this.shapes = shapeGroups(data);
          this.shapesSupported =
            this.shapes.length > 0 || turnsFrom(data).length === 0;
          this.shapesLoading = false;
        })
        .catch((err) => {
          this.shapesLoading = false;
          this.shapes = [];
          this.shapesError = this.describeError(err, "Shapes");
        });
    },
    toggle(id) {
      // Replaced rather than mutated: Vue 3 tracks the object, and a new one
      // keeps the JSON toggle honest if the row list is swapped underneath it.
      this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
    },

    showTurns() {
      const section = this.$refs.turnsSection;
      if (section && section.scrollIntoView)
        section.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    searchLater() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.onFilter();
      }, SEARCH_DEBOUNCE_MS);
    },

    /**
     * Turn a `voiceFetch` failure into copy that is true ON THIS PAGE.
     *
     * Only the classes whose standard sentence would mislead here are
     * rewritten; everything else keeps the wording every other voice surface
     * uses, so a session that has ended says the same thing in both places.
     */
    describeError(err, what) {
      const cls = err && err.voiceClass;
      if (cls == "missing")
        return {
          title: `${what} not available.`,
          detail: "This server does not have the voice usage endpoints yet.",
          next: "They arrive with the backend release that adds the voice turn log.",
          danger: false,
        };
      if (cls == "notEnabled")
        return {
          title: `${what} not shown.`,
          detail: "This page is for super admins only.",
          next: "If you should be able to see it, ask for the super-admin flag on your account.",
          danger: false,
        };
      return {
        title: `${what} not shown.`,
        detail: (err && err.detail) || "Your device could not reach the server.",
        next: (err && err.next) || "Check your connection and try again.",
        danger: !err || err.uiState != "warning",
      };
    },

    reload() {
      this.now = new Date();
      if (this.range.pending) return; // half-filled custom range: ask, do not guess
      this.loadOverview();
      // Whichever of the two views is on screen. Reloading the turns under a
      // visible shape list would leave last range's shapes over this range's
      // heading — the one mistake this page cannot afford.
      this.onFilter();
    },

    loadOverview() {
      this.overviewLoading = true;
      this.overviewError = null;

      return this.$store
        .dispatch("fetchVoiceAdmin", { view: "overview", range: this.range })
        .then((data) => {
          this.overview = shapeOverview(data);
          this.overviewLoading = false;
        })
        .catch((err) => {
          this.overviewLoading = false;
          // The old numbers are dropped rather than left on screen under a new
          // range's heading, which would attach the right label to the wrong
          // period — the one mistake this page cannot afford.
          this.overview = null;
          this.overviewError = this.describeError(err, "Voice usage");
        });
    },

    /** @param {boolean} append true when this is the "Load more" page. */
    loadTurns(append) {
      if (this.range.pending) return Promise.resolve();
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }

      const filters = { ...this.turnFilters };
      if (append && this.turnsBefore) filters.before = this.turnsBefore;
      else this.turnsBefore = null;

      this.turnsLoading = true;
      this.turnsError = null;

      return this.$store
        .dispatch("fetchVoiceAdmin", { view: "turns", filters })
        .then((data) => {
          const rows = shapeTurns(turnsFrom(data));
          this.turns = append ? mergeTurns(this.turns, rows) : rows;
          // The cursor is the server's `nextBefore` when it sends one, and is
          // otherwise worked out from THIS page rather than the merged list: a
          // page that arrived short means the end of the data even when the
          // pages before it were full.
          this.turnsBefore = cursorFrom(data, rows, filters.limit);
          this.turnsLoading = false;
        })
        .catch((err) => {
          this.turnsLoading = false;
          if (!append) this.turns = [];
          this.turnsError = this.describeError(err, "Turns");
        });
    },
  },

  mounted() {
    this.reload();
  },

  unmounted() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
};
</script>

<style scoped>
/* Plain CSS, prefixed `sa-voice-admin-`. `public/assets/styles.css` is a
   committed, pre-compiled Tailwind artefact that the Vue build does not
   regenerate, so an unlisted utility class here would do nothing at runtime. */

.sa-voice-admin {
  padding: 0.75rem 0.875rem 4rem;
  font-family: Montserrat, sans-serif;
  color: #0c4a6e;
  text-align: left;
}

.sa-voice-admin-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
}

.sa-voice-admin-h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
}

.sa-voice-admin-h2 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 0.625rem;
  font-size: 16px;
  font-weight: 600;
}

.sa-voice-admin-sub,
.sa-voice-admin-rangeline {
  margin: 0.125rem 0 0;
  font-size: 13px;
  color: #475569;
}

.sa-voice-admin-rangeline {
  margin-bottom: 0.875rem;
}

.sa-voice-admin-muted {
  font-size: 13px;
  font-weight: 400;
  color: #475569;
}

/* ---- controls ---------------------------------------------------------- */

.sa-voice-admin-range {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
}

.sa-voice-admin-chip {
  min-height: 34px;
  padding: 0.25rem 0.75rem;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.sa-voice-admin-chip-on {
  background: #0c4a6e;
  color: #ffffff;
}
.sa-voice-admin-chip-small {
  min-height: 26px;
  padding: 0 0.5rem;
  font-size: 11px;
  border-width: 1px;
}
.sa-voice-admin-chip:disabled {
  opacity: 0.5;
  cursor: default;
}

.sa-voice-admin-btn {
  margin-top: 0.75rem;
  min-height: 44px;
  padding: 0.5rem 1.25rem;
  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #ffffff;
  color: #0c4a6e;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.sa-voice-admin-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* Focus is never removed, only made obvious: this page is meant to be driven
   from the keyboard, and the tables are focus targets in their own right. */
.sa-voice-admin button:focus-visible,
.sa-voice-admin input:focus-visible,
.sa-voice-admin select:focus-visible,
.sa-voice-admin [tabindex]:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 1px;
}

.sa-voice-admin-custom {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

.sa-voice-admin-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.sa-voice-admin input,
.sa-voice-admin select {
  min-height: 34px;
  max-width: 16rem;
  padding: 0.25rem 0.375rem;
  border: 1px solid #94a3b8;
  border-radius: 4px;
  font-family: inherit;
  font-size: 13px;
  color: #0c4a6e;
  background: #ffffff;
}

.sa-voice-admin-check {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}
.sa-voice-admin-check input {
  min-height: 0;
}

/* ---- panels ------------------------------------------------------------ */

.sa-voice-admin-panel {
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 1rem;
  background: #ffffff;
}
.sa-voice-admin-panel-warning {
  border-color: #facc15;
  background: #fefce8;
}
.sa-voice-admin-panel-danger {
  border-color: #f87171;
  background: #fef2f2;
}
.sa-voice-admin-panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
.sa-voice-admin-panel-detail {
  margin: 0.25rem 0 0;
  font-size: 14px;
  line-height: 1.45;
}
.sa-voice-admin-panel-next {
  margin: 0.375rem 0 0;
  font-size: 13px;
  color: #475569;
  line-height: 1.45;
}

.sa-voice-admin-card {
  margin-top: 1rem;
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 0.875rem;
  background: #ffffff;
}

.sa-voice-admin-note {
  margin: 0.625rem 0 0;
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
}
/* Marked, not alarmed. The numbers are correct and the system is working; what
   is missing is history, so this is the same warning yellow a refusal gets and
   never the danger red of a fault. */
.sa-voice-admin-note-flag {
  border-left: 4px solid #facc15;
  padding: 0.25rem 0 0.25rem 0.5rem;
  background: #fefce8;
}

/* ---- tiles ------------------------------------------------------------- */

.sa-voice-admin-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.625rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.sa-voice-admin-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 0.5rem 0.625rem;
  background: #ffffff;
}
.sa-voice-admin-tile-label {
  font-size: 12px;
  font-weight: 500;
  color: #475569;
}
.sa-voice-admin-tile-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
}
.sa-voice-admin-tile-sub {
  font-size: 11px;
  color: #475569;
}

.sa-voice-admin-tile-pop {
  cursor: default;
}
.sa-voice-admin-pop {
  display: none;
  position: absolute;
  z-index: 40;
  top: calc(100% + 4px);
  right: 0;
  width: 17rem;
  border: 2px solid #0c4a6e;
  border-radius: 4px;
  padding: 0.5rem 0.625rem;
  background: #ffffff;
  box-shadow: 0 6px 12px -4px rgba(0, 0, 0, 0.25);
}
/* Focus as well as hover: a hover-only disclosure does not exist for anyone
   driving this page from the keyboard. */
.sa-voice-admin-tile-pop:hover .sa-voice-admin-pop,
.sa-voice-admin-tile-pop:focus-within .sa-voice-admin-pop {
  display: block;
}
.sa-voice-admin-pop-title {
  margin: 0 0 0.25rem;
  font-size: 12px;
  font-weight: 700;
}
.sa-voice-admin-pop-row {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.125rem 0;
}
.sa-voice-admin-pop-name {
  flex: none;
  width: 4.5rem;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
}
.sa-voice-admin-pop-note {
  margin: 0.375rem 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: #475569;
}
.sa-voice-admin-pop-value {
  flex: none;
  width: 4.5rem;
  text-align: right;
  font-size: 11px;
  font-weight: 700;
}

/* ---- bars -------------------------------------------------------------- */

.sa-voice-admin-bar {
  /* `display: block` is load-bearing, not tidiness. A flex row blockifies its
     children, so the funnel bars worked; the refusal bar is a plain child of a
     block and, left inline, a span ignores `height` and draws nothing. */
  display: block;
  flex: 1 1 auto;
  height: 10px;
  border-radius: 2px;
  background: #e2e8f0;
  overflow: hidden;
}
.sa-voice-admin-bar-tall {
  height: 16px;
}
.sa-voice-admin-bar-fill {
  display: block;
  height: 100%;
  background: #0c4a6e;
}
.sa-voice-admin-bar-warn {
  background: #facc15;
}

.sa-voice-admin-cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.625rem;
}
.sa-voice-admin-cols .sa-voice-admin-card {
  margin-top: 0;
}

.sa-voice-admin-funnel-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
}
.sa-voice-admin-funnel-name {
  flex: none;
  width: 5.5rem;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sa-voice-admin-funnel-value {
  flex: none;
  width: 4.5rem;
  text-align: right;
  font-size: 14px;
  font-weight: 700;
}

.sa-voice-admin-refusal {
  padding: 0.25rem 0;
}
.sa-voice-admin-refusal-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.1875rem;
}
/* Refusal codes are long SCREAMING_SNAKE constants and the code IS the answer,
   so it gets the width rather than being elided to a prefix two codes share. */
.sa-voice-admin-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sa-voice-admin-refusal-value {
  flex: none;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.sa-voice-admin-funnel-sub {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: #475569;
}

.sa-voice-admin-stack {
  display: flex;
  height: 18px;
  border-radius: 2px;
  background: #e2e8f0;
  overflow: hidden;
}
.sa-voice-admin-stack-seg {
  display: block;
  height: 100%;
}
.sa-voice-admin-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 0.25rem;
}
/* The four arms. Colour is never the only signal — each legend line prints its
   own count, and every segment carries a title. */
.sa-voice-admin-src-grammar {
  background: #0c4a6e;
}
.sa-voice-admin-src-jev {
  background: #0e7490;
}
.sa-voice-admin-src-gemini {
  background: #b45309;
}
.sa-voice-admin-src-codex {
  background: #6d28d9;
}

.sa-voice-admin-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.875rem;
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  color: #475569;
}
.sa-voice-admin-legend strong {
  color: #0c4a6e;
  margin: 0 0.25rem;
}

/* ---- tables ------------------------------------------------------------ */

.sa-voice-admin-scroll {
  overflow-x: auto;
}

.sa-voice-admin-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sa-voice-admin-table th {
  position: sticky;
  top: 0;
  background: #0c4a6e;
  color: #ffffff;
  text-align: right;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
}
.sa-voice-admin-table th:first-child,
.sa-voice-admin-table td:first-child {
  text-align: left;
}
.sa-voice-admin-table td {
  padding: 0.3125rem 0.5rem;
  text-align: right;
  border-top: 1px solid #e2e8f0;
  white-space: nowrap;
}
.sa-voice-admin-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.sa-voice-admin-sort {
  border: 0;
  background: none;
  padding: 0;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.sa-voice-admin-row {
  cursor: pointer;
}
.sa-voice-admin-row:hover {
  background: #e0f2fe;
}
.sa-voice-admin-row-on,
.sa-voice-admin-row-on:nth-child(even) {
  background: #bae6fd;
}
.sa-voice-admin-name {
  font-weight: 600;
}
.sa-voice-admin-email {
  display: block;
  font-size: 11px;
  font-weight: 400;
  color: #475569;
}

/* ---- stages, abstains, arms, shapes ------------------------------------ */

/* Two cards, the wide one first: nine stage labels are sentences and need the
   room, while the abstain list is a code and a number. */
.sa-voice-admin-pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.625rem;
  margin-top: 1rem;
}
.sa-voice-admin-pair .sa-voice-admin-card {
  margin-top: 0;
}

.sa-voice-admin-stage {
  padding: 0.3125rem 0;
}
.sa-voice-admin-stage-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.1875rem;
}
.sa-voice-admin-stage-name {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sa-voice-admin-stage-value {
  flex: none;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}

/* One track, two bars, both from the left edge. The p95 bar is drawn first and
   the p50 bar sits on top of it, so the pale remainder IS the spread — and
   because both are measured from the same origin on the same scale, the eye
   compares rows rather than reading nine separate pictures. */
.sa-voice-admin-track {
  position: relative;
  display: block;
  height: 14px;
  border-radius: 2px;
  background: #e2e8f0;
  overflow: hidden;
}
.sa-voice-admin-track-p95 {
  position: absolute;
  inset: 0 auto 0 0;
  display: block;
  background: #7dd3fc;
}
.sa-voice-admin-track-p50 {
  position: absolute;
  inset: 0 auto 0 0;
  display: block;
  background: #0c4a6e;
}
/* A bar that is off the scale says so at its own right edge, rather than
   quietly ending flush like a measured one. Same warning yellow the funnel gap
   and the refusal bars use: the number is correct, the reading of it is what
   needs care. */
.sa-voice-admin-track-over {
  border-right: 4px solid #facc15;
}

/* A control, styled as a row. `text-align: left` and `width: 100%` because a
   button is centred and shrink-to-fit by default, which would turn a list of
   comparable bars into a ragged column of different widths. */
.sa-voice-admin-abstain {
  display: block;
  width: 100%;
  border: 0;
  border-radius: 3px;
  padding: 0.25rem 0.25rem 0.3125rem;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.sa-voice-admin-abstain:hover,
.sa-voice-admin-abstain:focus-visible {
  background: #f1f5f9;
}
.sa-voice-admin-abstain-on {
  background: #e0f2fe;
  box-shadow: inset 0 0 0 2px #0c4a6e;
}
.sa-voice-admin-abstain-label {
  font-size: 13px;
  font-weight: 600;
}
.sa-voice-admin-abstain .sa-voice-admin-code {
  display: block;
  margin-bottom: 0.1875rem;
}

.sa-voice-admin-arms {
  margin: 0.25rem 0 0;
  padding: 0;
  list-style: none;
}
.sa-voice-admin-arms li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.125rem 0;
}
.sa-voice-admin-arm-name {
  flex: none;
  width: 4rem;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.sa-voice-admin-arm-value {
  flex: none;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.sa-voice-admin-shapes {
  margin: 0;
  padding: 0;
  list-style: none;
}
.sa-voice-admin-shape {
  border-top: 1px solid #e2e8f0;
  padding: 0.5rem 0;
}
.sa-voice-admin-shape-head {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}
.sa-voice-admin-shape-count {
  flex: none;
  min-width: 2.5rem;
  font-size: 16px;
  font-weight: 700;
}
/* The shape is machine vocabulary — N, P, T — so it is set as code. */
.sa-voice-admin-shape-name {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  word-break: break-word;
}
.sa-voice-admin-shape-example {
  display: block;
  margin-top: 0.25rem;
  border: 0;
  border-radius: 3px;
  padding: 0.125rem 0.25rem;
  background: none;
  color: #0c4a6e;
  font: inherit;
  font-size: 13px;
  font-style: italic;
  text-align: left;
  cursor: pointer;
}
.sa-voice-admin-shape-example:hover,
.sa-voice-admin-shape-example:focus-visible {
  background: #f1f5f9;
  text-decoration: underline;
}

/* The escalation trail and the per-turn spans: dense, wrapping, and never
   competing with the transcript above them for attention. */
.sa-voice-admin-arms-trail,
.sa-voice-admin-turn-timing {
  margin: 0.25rem 0 0;
  font-size: 12px;
  color: #475569;
  line-height: 1.6;
}
.sa-voice-admin-turn-timing span {
  display: inline-block;
  margin-right: 0.75rem;
  white-space: nowrap;
}

/* ---- turns ------------------------------------------------------------- */

.sa-voice-admin-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.625rem;
  margin-bottom: 0.75rem;
}

.sa-voice-admin-field {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.sa-voice-admin-turns {
  margin: 0;
  padding: 0;
  list-style: none;
}

.sa-voice-admin-turn {
  border-top: 1px solid #e2e8f0;
  padding: 0.5rem 0;
}
.sa-voice-admin-turn-refused {
  border-left: 4px solid #facc15;
  padding-left: 0.5rem;
}

.sa-voice-admin-turn-head,
.sa-voice-admin-turn-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem 0.625rem;
  font-size: 12px;
  color: #475569;
}
.sa-voice-admin-turn-at {
  min-width: 5.5rem;
  font-weight: 600;
  color: #0c4a6e;
}
.sa-voice-admin-turn-who {
  font-weight: 600;
  color: #0c4a6e;
}
.sa-voice-admin-turn-who .sa-voice-admin-email {
  display: inline;
  margin-left: 0.25rem;
}
.sa-voice-admin-turn-meta {
  margin-top: 0.25rem;
}

.sa-voice-admin-tag {
  border: 1px solid #94a3b8;
  border-radius: 9999px;
  padding: 0 0.4375rem;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.sa-voice-admin-tag-warn {
  border-color: #b45309;
  background: #fefce8;
  color: #b45309;
}
/* The abstain code on a row is a real button — it filters the log — so it
   inherits the tag's shape and takes back the four things a <button> changes:
   its own font, its centring, its background and the pointer it does not get. */
.sa-voice-admin-tag-link {
  background: none;
  color: #0c4a6e;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  line-height: 1.5;
  cursor: pointer;
}
.sa-voice-admin-tag-link:hover,
.sa-voice-admin-tag-link:focus-visible {
  background: #e0f2fe;
}

.sa-voice-admin-transcript {
  margin: 0.25rem 0 0;
  font-size: 15px;
  line-height: 1.4;
  color: #0c4a6e;
  /* The transcript is evidence: it wraps and keeps its own spacing rather than
     being collapsed or truncated to fit a row. */
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.sa-voice-admin-parse strong {
  font-size: 13px;
}
.sa-voice-admin-args {
  margin-left: 0.375rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
}

.sa-voice-admin-reason,
.sa-voice-admin-readback {
  margin: 0.25rem 0 0;
  font-size: 12px;
  color: #475569;
  line-height: 1.4;
}
.sa-voice-admin-readback {
  font-style: italic;
}

.sa-voice-admin-json {
  margin: 0.5rem 0 0;
  padding: 0.5rem;
  max-height: 22rem;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #f8fafc;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.45;
  white-space: pre;
}

.sa-voice-admin-more {
  margin-top: 0.75rem;
}

@media (min-width: 720px) {
  .sa-voice-admin-tiles {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1100px) {
  .sa-voice-admin-tiles {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
  .sa-voice-admin-cols {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  /* Two thirds to the stages: their labels are sentences, and elided sentences
     are the reason a reader gives up on a card. */
  .sa-voice-admin-pair {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  }
}
</style>
